"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { api, type EventoDto } from "@/lib/api";
import { resolveImageUrl } from "@/lib/media-url";
import { ScrollReveal } from "@/components/ScrollReveal";
import { groupMediaItems } from "@/lib/evento-media";

interface EventsGridBlockProps {
  title: string;
  maxItems?: number;
  showArchiveLink?: boolean;
  archiveLinkUrl?: string;
  archiveLinkText?: string;
  className?: string;
}

function EventCard({ e }: { e: EventoDto }) {
  const thumbSrc = resolveImageUrl(e.foto) || resolveImageUrl(e.volantino);
  const d = new Date(e.data);
  const month = d.toLocaleDateString("it-IT", { month: "short" }).toUpperCase();
  const day = d.getDate();

  return (
    <article className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100">
      <a href={`/eventi/${e.slug}`} className="block">
        <div className="relative overflow-hidden bg-neutral-50">
          {thumbSrc ? (
            // EventsGridBlock e' client-side fetched (useEffect) e ha un
            // loading skeleton: niente priority qui — Next/Image fa lazy
            // automatico sotto la viewport. Sizes per grid 2/3/4/5 col.
            <Image
              src={thumbSrc}
              alt={e.titolo}
              width={800}
              height={1100}
              sizes="(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
              className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500"
            />
          ) : (
            <div className="w-full aspect-video flex items-center justify-center text-neutral-300">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className="absolute top-2 right-2 bg-[#4a90c4] text-white rounded-lg px-2 py-1 text-center leading-tight shadow-md">
            <div className="text-[9px] font-bold uppercase tracking-wide">{month}</div>
            <div className="text-lg font-bold leading-none">{day}</div>
          </div>
          {/* A11y + Design-FIX (badge "In evidenza"):
              - amber-500+white falliva contrasto (1.94:1)
              - amber-800 passava ma si confondeva con la category badge "cultura" (anch'essa amber-800)
              - bg-[#1a3a5c] (brand blue) + text-white = ~10:1, semanticamente "meta/sistema",
                fuori dalla palette categorie quindi nessun conflitto */}
          {e.daNonPerdere && (
            <div className="absolute top-2 left-2 bg-[#1a3a5c] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shadow-sm">
              In evidenza
            </div>
          )}
        </div>
      </a>
      <div className="p-3 sm:p-4">
        <a href={`/eventi/${e.slug}`}>
          <h3 className="font-bold text-sm text-[#1a3a5c] group-hover:text-[#4a90c4] transition-colors line-clamp-2 leading-snug">
            {e.titolo}
          </h3>
        </a>
        <p className="text-xs text-neutral-500 mt-1.5">
          {d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
          {e.luogo && <> &mdash; {e.luogo}</>}
        </p>
        {/* Icon-strip allegati media (V13) */}
        {(() => {
          const groups = groupMediaItems(e.mediaItems);
          if (groups.length === 0) return null;
          return (
            <div className="mt-2 flex flex-wrap gap-1">
              {groups.map((g) => (
                <a
                  key={g.key}
                  href={`/eventi/${e.slug}#media-${g.key === "foto" ? "foto" : g.key === "video" ? "videos" : "materiali"}`}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${g.meta.pill} hover:opacity-80 transition-opacity`}
                  title={`${g.count} ${g.count === 1 ? g.meta.label : g.meta.labelPlural}`}
                  // A11y-FIX (Lighthouse "I link identici hanno lo stesso scopo"):
                  // includere il titolo dell'evento nell'aria-label per distinguere
                  // i pill "🎬 1" su card diverse. Pre-fix tutti = "1 Video".
                  aria-label={`${g.count} ${g.count === 1 ? g.meta.label : g.meta.labelPlural} — ${e.titolo}`}
                >
                  <span aria-hidden>{g.meta.icon}</span>
                  <span>{g.count}</span>
                </a>
              ))}
            </div>
          );
        })()}
      </div>
    </article>
  );
}

export function EventsGridBlock({
  title,
  maxItems = 10,
  showArchiveLink = true,
  archiveLinkUrl = "/eventi?filter=past",
  archiveLinkText = "Archivio completo →",
  className,
}: EventsGridBlockProps) {
  const [events, setEvents] = useState<EventoDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [upcoming, past] = await Promise.all([
          api.eventi.list({ size: 6, filter: "upcoming" }),
          api.eventi.list({ size: 10, filter: "past" }),
        ]);
        const all: EventoDto[] = [];
        const seen = new Set<number>();
        for (const ev of [...upcoming.items, ...past.items]) {
          if (seen.has(ev.id)) continue;
          seen.add(ev.id);
          all.push(ev);
        }
        if (!cancelled) setEvents(all);
      } catch {
        if (!cancelled) setEvents([]);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (events === null) {
    return (
      <section className={`bg-neutral-50/80 ${className || ""}`}>
        <div className="px-4 sm:px-8 py-16 max-w-7xl mx-auto">
          <div className="h-8 w-48 bg-neutral-200 rounded animate-pulse mb-10" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-neutral-200 rounded-xl h-48 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (events.length === 0) return null;

  return (
    <section className={`bg-neutral-50/80 ${className || ""}`}>
      <div className="px-4 sm:px-8 py-16 max-w-7xl mx-auto">
        <ScrollReveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1a3a5c] uppercase tracking-wide">
                {title}
              </h2>
              <div className="mt-3 h-[3px] w-16 bg-[#4a90c4] rounded-full" />
            </div>
            {showArchiveLink && archiveLinkUrl && (
              <a
                href={archiveLinkUrl}
                className="text-sm font-semibold text-[#4a90c4] hover:text-[#1a3a5c] transition-colors uppercase tracking-wide"
              >
                {archiveLinkText}
              </a>
            )}
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
          {events.slice(0, maxItems).map((e, i) => (
            <ScrollReveal key={e.id} delay={i * 80}>
              <EventCard e={e} />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

